// src/modules/app-views/products/pages/Document/tabs/StockTab/StockMovementsCard.jsx
// *******************************************************************************
// STOCK MOVEMENTS CONTENT
// *******************************************************************************
// Paginated movement history. Rendered inside Collapse by StockTab/index.jsx.
// Columns: Data/Autor | Tipo | Documento | Descrição | Emissor | Estoque | Info | Qde | Saldo | Ações
// Edit rules: only manual movements by current user, qty/warehouse only if last.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useCallback } from 'react';
import { Tag, Typography, Tooltip, Button } from 'antd';
import { DownloadOutlined, UploadOutlined, SwapOutlined, DesktopOutlined, EditOutlined } from '@ant-design/icons';
import { MdOutlineKeyboardAlt } from 'react-icons/md';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleTable from 'components/SimpleComponents/SimpleTable';
import { openModal } from 'components/core/GenericModal';
import axiosService from 'services/axiosService';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';
import dayjs from 'dayjs';
import StockMovementModal from '../../modals/StockMovementModal';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const StockMovementsCard = ({ refreshToken, onRefresh }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, viewMode } = useDocumentContext();
  const storedUser = useZustand('auth', 'user');
  const productId = data?._id;
  const currentAccountId = storedUser?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchMovements = useCallback(
    async (page = 1, limit = 10) => {
      if (!productId) return;

      setLoading(true);
      try {
        const url = `${API.STOCK_MOVEMENTS.BY_PRODUCT}?productId=${productId}&page=${page}&limit=${limit}`;
        const [result] = await axiosService.get({ url, returnAsObject: true });

        if (result?.docs) {
          setMovements(result.docs);
          setPagination({ current: result.page, pageSize: result.limit, total: result.total });
        }
      } catch (error) {
        console.error('[StockMovementsCard] Fetch error:', error);
      } finally {
        setLoading(false);
      }
    },
    [productId],
  );

  const handleEdit = useCallback(
    (record, index) => {
      const isLast = index === 0; // First in list = most recent (sorted desc)
      openModal(StockMovementModal, {
        productId,
        stockQuantity: data?.stock?.quantity || 0,
        allowNegativeStock: data?.stock?.allowNegativeStock || false,
        editingMovement: record,
        isLastMovement: isLast,
        onSuccess: () => {
          fetchMovements(pagination.current, pagination.pageSize);
          onRefresh?.();
        },
      });
    },
    [productId, data, fetchMovements, pagination, onRefresh],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchMovements();
  }, [fetchMovements, refreshToken]);

  const handleTableChange = useCallback(
    pag => {
      fetchMovements(pag.current, pag.pageSize);
    },
    [fetchMovements],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const isLocked = viewMode === 'view';

  const columns = [
    // 1. DATA + AUTOR
    {
      title: translateX('date').toUpperCase(),
      dataIndex: ['_metadata', 'audit', 'createdAt'],
      width: 130,
      render: (value, record) => (
        <div>
          <div>{value ? dayjs(value).format('DD/MM/YY HH:mm') : '—'}</div>
          {record.__author?.name && (
            <Text className="text-muted" style={{ fontSize: 11 }}>
              {record.__author.name}
            </Text>
          )}
        </div>
      ),
    },
    // 2. TIPO
    {
      title: translateX('type').toUpperCase(),
      dataIndex: 'movementType',
      width: 90,
      align: 'center',
      render: (type, record) => {
        if (record.isReversal) {
          return <Tag icon={<SwapOutlined />} color="red">{translateX('reversal')}</Tag>;
        }
        if (type === 'entry') {
          return <Tag icon={<DownloadOutlined />} color="green">{translateX('entry')}</Tag>;
        }
        if (type === 'exit') {
          return <Tag icon={<UploadOutlined />} color="red">{translateX('stock_exit')}</Tag>;
        }
        return <Tag>{type}</Tag>;
      },
    },
    // 3. DOCUMENTO
    {
      title: translateX('document').toUpperCase(),
      dataIndex: 'documentNumber',
      width: 90,
      align: 'center',
      render: value => value || '—',
    },
    // 4. DESCRIÇÃO
    {
      title: translateX('description').toUpperCase(),
      dataIndex: 'notes',
      ellipsis: true,
      render: value => value || '—',
    },
    // 5. EMISSOR
    {
      title: translateX('issuer').toUpperCase(),
      dataIndex: 'entityName',
      ellipsis: true,
      render: (name, record) => {
        if (!name) return '—';
        return (
          <div>
            <div style={{ textTransform: 'uppercase' }}>
              <Text strong ellipsis>{name}</Text>
            </div>
            {record.entityCpfCnpj && (
              <Text className="text-muted" style={{ fontSize: 11 }}>
                {formatCpfCnpj(record.entityCpfCnpj)}
              </Text>
            )}
          </div>
        );
      },
    },
    // 6. ESTOQUE
    {
      title: translateX('warehouse').toUpperCase(),
      dataIndex: 'stockName',
      width: 120,
      render: (name, record) => {
        const code = record.stockCode;
        return code ? `${code}-${name}` : name || '—';
      },
    },
    // 7. INFO
    {
      title: 'INFO',
      dataIndex: ['_metadata', 'origin'],
      width: 50,
      align: 'center',
      render: value => {
        const isManual = value === 0;
        return (
          <Tooltip title={isManual ? translateX('manual') : translateX('automatic')}>
            {isManual
              ? <MdOutlineKeyboardAlt style={{ fontSize: 16, color: '#8c8c8c' }} />
              : <DesktopOutlined style={{ fontSize: 14, color: '#8c8c8c' }} />}
          </Tooltip>
        );
      },
    },
    // 8. QDE
    {
      title: 'QDE',
      dataIndex: 'quantity',
      width: 70,
      align: 'right',
      render: value => (
        <Text style={{ color: value > 0 ? '#52c41a' : value < 0 ? '#ff4d4f' : '#8c8c8c' }}>
          {value > 0 ? '+' : ''}{value}
        </Text>
      ),
    },
    // 9. SALDO
    {
      title: translateX('balance').toUpperCase(),
      dataIndex: 'balanceAfter',
      width: 70,
      align: 'right',
      render: value => <Text strong>{value}</Text>,
    },
    // 10. AÇÕES
    {
      title: '',
      width: 50,
      align: 'center',
      render: (_, record, index) => {
        const canEdit = canEditMovement(record, currentAccountId);
        if (!canEdit) return null;

        return (
          <Tooltip title={translateX('edit')}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              disabled={isLocked}
              onClick={() => handleEdit(record, index)}
            />
          </Tooltip>
        );
      },
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="compact-cost-table">
    <SimpleTable
      columns={columns}
      dataSource={movements}
      rowKey="_id"
      loading={loading}
      pagination={{
        ...pagination,
        size: 'small',
        showSizeChanger: false,
        showTotal: total => `${total} ${translateX('movements')}`,
      }}
      onChange={handleTableChange}
      size="small"
      scroll={{ x: 900 }}
      locale={{ emptyText: translateX('no_movements') }}
    />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default StockMovementsCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Check if current user can edit a movement.
 * Rules: must be manual + author must be current user + not a reversal.
 */
function canEditMovement(record, currentAccountId) {
  if (!record || !currentAccountId) return false;
  if (record.sourceType !== 'manual') return false;
  if (record.isReversal) return false;
  const authorId = record._metadata?.audit?.createdByAccountId;
  return String(authorId) === String(currentAccountId);
}

function formatCpfCnpj(value) {
  if (!value) return null;
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 14) {
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }
  if (digits.length === 11) {
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }
  return value;
}
